/**
 * Soft!=Live Page197 — Settings Sessions residual liquid (Page117/62 + dual).
 * Mount: /settings/sessions
 * Buses (contract ops — never painted in operator DOM):
 *   get__settings_sessions_status · get auth sessions · post sessions revoke
 * Soft residual deleted:
 *  - Soft per-device session table invent
 *  - Soft revoke success without POST seat
 *  - Soft listable=true invent when product refuses enumeration
 *  - Soft invent seats while posture loading
 *  - path /operation labels painted in DOM
 */
import { html, Ico, Card, Alert, KV, go } from "./ui.js";
import { path, useAction, useApi } from "./api.js";
import { envelopeSummary } from "./action-envelope.js";
import { useSession } from "./session.js";

/** Path-free operator copy for Sessions desk. Catalogue stays empty until Live says so. */
export function softSessionsSettingsErrorOf(err) {
  const n = String(err == null ? "" : err).trim();
  const low = n.toLowerCase();
  if (!low) return "That action could not be completed.";
  if (/\b403\b|forbidden|seat_gated|not_authorized|denied/.test(low)) {
    return "Your account cannot manage sessions here. Ask a manager if you need access.";
  }
  if (/\b401\b|unauthorized|missing bearer/.test(low)) {
    return "Sign in again to continue.";
  }
  if (/\b422\b|\b400\b|invalid|required/.test(low)) {
    return "That session action was not accepted.";
  }
  if (/\b429\b|too many/.test(low)) {
    return "Too many tries — wait a moment, then retry.";
  }
  if (/\b503\b|unavailable/.test(low)) {
    return "Session controls are not ready right now.";
  }
  if (/network|failed to fetch|timeout/.test(low)) {
    return "Network issue — try again in a moment.";
  }
  if (/\/api\/|\/auth\/|\/settings\/sessions|GET |POST |status_path|http/.test(low)) {
    return "That action could not be completed.";
  }
  if (n.length > 160) return n.slice(0, 157) + "…";
  return n;
}

/** Soft!=Live Page197: missing contract ops must not crash the desk. */
function safeSessionsPath(op, opts) {
  try {
    return path(op, opts);
  } catch (_e) {
    if (op === "get__settings_sessions_status") return "/settings/sessions/status";
    if (op === "get__auth_me_sessions") return "/auth/me/sessions";
    if (op === "post__auth_me_sessions_revoke") return "/auth/me/sessions/revoke";
    return "";
  }
}

/** Pure: seat chips from Live Settings Sessions posture (Page197) — fail-closed. */
export function sessionsSettingsSeatOf(post) {
  const p = post && typeof post === "object" ? post : {};
  return {
    honestyReadAllowed: p.honesty_read_allowed === true,
    revokeWriteAllowed: p.revoke_write_allowed === true,
    signOutThisAllowed: p.sign_out_this_allowed === true,
    passwordNavAllowed: p.password_nav_allowed === true,
    dangerNavAllowed: p.danger_nav_allowed === true,
    reReadAllowed: p.re_read_allowed === true,
    modelExpected: String(p.model_expected || "session_version_bump"),
    listableExpected: p.listable_expected === true,
    role: String(p.principal_role || ""),
    detail: p.detail || null,
    enabled: p.enabled === true,
    postureLabel: String(p.posture_label || "").trim(),
  };
}

/** Pure: KPIs from Live honesty pack · list invent forbidden. */
export function sessionsDeskKpisOf(pack, me) {
  const p = pack && typeof pack === "object" ? pack : {};
  const self = p.self && typeof p.self === "object" ? p.self : {};
  return {
    model: String(p.model || "—"),
    listable: !!p.listable,
    user: String(self.username || (me && me.username) || "—"),
    role: String(self.role || (me && me.role) || "—"),
    softFlag: !!p.soft_neq_live,
  };
}

export function SessionsSettingsDesk({ toast }) {
  const { me, reload } = useSession();
  const posture = useApi(safeSessionsPath("get__settings_sessions_status"));
  const post = posture.data || {};
  // Fail-closed while loading — seats fail-closed.
  const seat = sessionsSettingsSeatOf(posture.loading || !posture.data ? {} : post);
  const remote = useApi(
    seat.honestyReadAllowed ? safeSessionsPath("get__auth_me_sessions") : "",
  );
  const { run, busy, error, envelope } = useAction();
  const postureLabel = seat.postureLabel
    || (seat.honestyReadAllowed
      ? (seat.revokeWriteAllowed
        ? "Session honesty open · end others open"
        : "Session honesty open")
      : "Sessions desk sealed");

  const kpis = sessionsDeskKpisOf(remote.data, me);
  const denied = !!(remote.error && /\b40[13]\b|forbidden/i.test(remote.error || ""));

  const reRead = () => {
    if (!seat.reReadAllowed) {
      toast && toast("Refresh is not available on this seat.", "warn");
      return;
    }
    posture.reload && posture.reload();
    if (seat.honestyReadAllowed) remote.reload && remote.reload();
    reload && reload();
    toast && toast("Sessions refreshed", "go");
  };

  const signOutElsewhere = async () => {
    if (!seat.revokeWriteAllowed) {
      toast && toast("Ending other sessions is not available on this seat.", "warn");
      return;
    }
    if (!window.confirm(
      "End every other signed-in place for this account? This browser stays signed in.",
    )) return;
    const out = await run(safeSessionsPath("post__auth_me_sessions_revoke"), {
      method: "POST",
      body: {},
    });
    if (!out) {
      toast(softSessionsSettingsErrorOf(error) || "Could not end other sessions.", "bad");
      return;
    }
    if (out.kept_current_session) {
      toast("Other signed-in places ended. This browser stays open.", "warn");
    } else {
      toast("Every signed-in place ended, including this one.", "warn");
    }
    remote.reload && remote.reload();
    reload && reload();
  };

  const signOutThis = () => {
    if (!seat.signOutThisAllowed) {
      toast && toast("Sign out is not available on this seat.", "warn");
      return;
    }
    go("/auth/signout");
  };

  if (!posture.loading && !seat.honestyReadAllowed && !seat.revokeWriteAllowed) {
    return html`<div className="sess-set-desk sess-set-desk--liquid"
      data-cmc-page="page117-sessions" data-cmc-sessions="page62" data-cmc-residual="page197-sessions">
      <${Alert} tone="bad" icon="ban" title="Sessions sealed"
        say="Your seat cannot read or end sessions here. Nothing is invented when the seat is closed."/>
      ${seat.detail ? html`<p className="note">${postureLabel}</p>` : null}
      <button type="button" className="btn btn--ghost btn--sm"
        disabled=${!seat.reReadAllowed || posture.loading} onClick=${reRead}>
        <${Ico} d="refresh" w=${14}/> Refresh
      </button>
    </div>`;
  }

  return html`<div className="sess-set-desk sess-set-desk--liquid"
    data-cmc-page="page117-sessions" data-cmc-sessions="page62" data-cmc-residual="page197-sessions">
    <div className="sess-set-desk__rail">
      <div>
        <h2 className="sess-set-desk__title">Where you are signed in</h2>
      </div>
      <div className="btn-row sess-set-desk__acts">
        <button type="button" className="btn btn--ghost btn--sm"
          disabled=${busy || posture.loading || !seat.reReadAllowed}
          onClick=${reRead}>
          <${Ico} d="refresh" w=${14}/> Refresh
        </button>
        <button type="button" className="btn btn--ghost btn--sm"
          disabled=${!seat.passwordNavAllowed}
          onClick=${() => go("/settings/security")}>Password</button>
        <button type="button" className="btn btn--ghost btn--sm"
          disabled=${!seat.dangerNavAllowed}
          onClick=${() => go("/settings/danger")}>Danger zone</button>
      </div>
    </div>

    ${envelope ? html`<${KV} k="Last action" v=${envelopeSummary(envelope) || "—"} tone=${envelope.ok ? "hi" : "warn"}/>` : null}

    <div className="sess-set-desk__kpis" role="group" aria-label="Sessions posture">
      <div className="sess-set-desk__kpi" data-on=${seat.honestyReadAllowed ? "1" : "0"}>
        <span>Session honesty</span>
        <b>${seat.honestyReadAllowed ? "open" : "sealed"}</b>
        <em>read</em>
      </div>
      <div className="sess-set-desk__kpi" data-on=${seat.revokeWriteAllowed ? "1" : "0"}>
        <span>End others</span>
        <b>${seat.revokeWriteAllowed ? "open" : "sealed"}</b>
        <em>independent</em>
      </div>
      <div className="sess-set-desk__kpi" data-on=${kpis.listable ? "1" : "0"}>
        <span>Device list</span>
        <b>${!seat.honestyReadAllowed ? "—" : (remote.loading ? "…" : (kpis.listable ? "yes" : "no"))}</b>
        <em>${seat.listableExpected ? "expected" : "not listed"}</em>
      </div>
      <div className="sess-set-desk__kpi" data-on="0">
        <span>Seat</span>
        <b>${seat.role || kpis.role || "—"}</b>
        <em>${kpis.user}</em>
      </div>
    </div>
    ${posture.error ? html`
      <${Alert} tone="warn" title="Could not load session status"
        say=${softSessionsSettingsErrorOf(posture.error)}/>` : null}

    ${!seat.honestyReadAllowed ? html`
      <${Alert} tone="info" title="Session honesty sealed"
        say="Ending other sessions may still be available on this seat."/>` : null}

    ${denied ? html`
      <${Alert} tone="info" title="Session honesty unavailable"
        say="Could not read session honesty. Device list only from the server honesty pack."/>` : null}

    ${remote.error && !denied ? html`
      <${Alert} tone="warn" title="Session honesty refused"
        say=${softSessionsSettingsErrorOf(remote.error)}/>` : null}

    <${Card} title="This browser" meta=${me ? me.username : ""}>
      <div className="btn-row">
        <button type="button" className="btn btn--ghost btn--sm" data-cmc-signout="sessions"
          disabled=${!seat.signOutThisAllowed}
          onClick=${signOutThis}>
          <${Ico} d="logout" w=${14}/> Sign out this browser
        </button>
      </div>
    <//>

    <${Card} title="What ends a session">
      <div className="btn-row">
        <button type="button" className="btn btn--danger btn--sm"
          disabled=${busy || !seat.revokeWriteAllowed}
          onClick=${signOutElsewhere}
          title="End other signed-in places">
          ${busy ? "Ending other places…" : "Sign out everywhere else"}
        </button>
      </div>
      <p className="note">
        Ends every other browser and device for this account. This one stays when the
        server keeps the current session. When this seat is sealed, end-other is refused honestly.
      </p>
    <//>

    ${!kpis.listable && remote.data ? html`
      <${Card} title="Device list" meta="not published">
        <${Alert} tone="warn" title="No per-device catalogue"
          say="The server does not enumerate devices here. Session table only from Live. Admin kicks live under Platform."/>
      <//>` : null}
  </div>`;
}
